import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

const MessagePreviewPage: React.FC = () => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const alertShown = useRef(false);

  const triggerAlert = () => {
    if (alertShown.current) return;
    alertShown.current = true;
    setShowAlert(true);
    setLoading(false);
  };

  useEffect(() => {
    const url = sessionStorage.getItem("messagePreviewUrl");
    setPreviewUrl(url);
  }, []);

  // Catch any postMessages from the Facebook preview iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.includes("facebook.com")) return;
      const dataStr =
        typeof event.data === "string"
          ? event.data
          : JSON.stringify(event.data ?? "");
      if (
        dataStr.includes("1675002") ||
        dataStr.toLowerCase().includes("unauthorized") ||
        dataStr.toLowerCase().includes("logged_out") ||
        dataStr.toLowerCase().includes("not_authed")
      ) {
        triggerAlert();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // FB.getLoginStatus — requires HTTPS; on HTTP the SDK blocks the call
  useEffect(() => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    const apiVersion = process.env.REACT_APP_FACEBOOK_API_VERSION || "v18.0";
    if (!appId) return;

    const checkFBLogin = () => {
      try {
        window.FB.init({ appId, version: apiVersion, cookie: true, xfbml: false });
        // force:true bypasses cached status for an accurate real-time check
        window.FB.getLoginStatus((response: any) => {
          if (response.status === "unknown") {
            triggerAlert();
          }
        }, true);
      } catch (_e) {
        // SDK blocks getLoginStatus on HTTP — no-op, alert will not show on HTTP
      }
    };

    if (window.FB) {
      checkFBLogin();
    } else {
      window.fbAsyncInit = checkFBLogin;
      const script = document.createElement("script");
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
      return () => {
        if (document.body.contains(script)) document.body.removeChild(script);
      };
    }
  }, []);

  if (!previewUrl) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <Typography color="text.secondary">No preview available.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f2f5",
        gap: 2,
      }}>
      <Typography variant="h6" sx={{ color: "#555", fontWeight: 600 }}>
        {showAlert ? "Message Preview Failed" : "Message Preview"}
      </Typography>
      {showAlert && (
        <Alert severity="error" sx={{ width: 450 }}>
          You are not logged into Facebook. Please log in to Facebook and try
          again.
        </Alert>
      )}
      {!showAlert && loading && <CircularProgress />}
      {!showAlert && (
        <iframe
          src={previewUrl}
          title="Message Preview"
          width={450}
          height={500}
          style={{ border: "none", overflow: "auto", display: loading ? "none" : "block" }}
          allow="autoplay"
          onLoad={() => setLoading(false)}
          onError={() => triggerAlert()}
        />
      )}
    </Box>
  );
};

export default MessagePreviewPage;
